// Analysis chart builders for the Live Transcoding analysis page.
// Number / format logic is byte-equivalent to the legacy analysisUtils.ts.

import type {
  LmsJobCountsUsage,
  LmsRecordingDurationUsage,
  LmsTranscodingDurationUsage,
} from '@/api/media/live-analysis-types';
import type { ChartSeries } from '@vnetwork/ui/charts';

const MONTH_LABELS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const RECORDING_LABELS: Record<string, string> = {
  total: 'Total',
  audioOnly: 'Audio Only',
  transmux: 'Transmux',
  h264SdTranscoding: 'H.264 SD',
  h264HdTranscoding: 'H.264 HD',
  h264UhdTranscoding: 'H.264 UHD',
  h265SdTranscoding: 'H.265 SD',
  h265HdTranscoding: 'H.265 HD',
  h265UhdTranscoding: 'H.265 UHD',
  gpuH264SdTranscoding: 'GPU H.264 SD',
  gpuH264HdTranscoding: 'GPU H.264 HD',
  gpuH264UhdTranscoding: 'GPU H.264 UHD',
  gpuH265SdTranscoding: 'GPU H.265 SD',
  gpuH265HdTranscoding: 'GPU H.265 HD',
  gpuH265UhdTranscoding: 'GPU H.265 UHD',
  sdTransmux: 'SD Transmux',
  hdTransmux: 'HD Transmux',
  uhdTransmux: 'UHD Transmux',
};

const TRANSCODING_LABELS: Record<string, string> = {
  total: 'Total',
  audio_only: 'Audio Only',
  transmux: 'Transmux',
  h264_sd_transcoding: 'H.264 SD',
  h264_hd_transcoding: 'H.264 HD',
  h264_uhd_transcoding: 'H.264 UHD',
  h265_sd_transcoding: 'H.265 SD',
  h265_hd_transcoding: 'H.265 HD',
  h265_uhd_transcoding: 'H.265 UHD',
  gpu_h264_sd_transcoding: 'GPU H.264 SD',
  gpu_h264_hd_transcoding: 'GPU H.264 HD',
  gpu_h264_uhd_transcoding: 'GPU H.264 UHD',
  gpu_h265_sd_transcoding: 'GPU H.265 SD',
  gpu_h265_hd_transcoding: 'GPU H.265 HD',
  gpu_h265_uhd_transcoding: 'GPU H.265 UHD',
};

const JOB_LABELS: Record<string, string> = {
  h264MaxConcurrentNum: 'Max H.264 Concurrent',
  h265MaxConcurrentNum: 'Max H.265 Concurrent',
  linear: 'Linear Jobs',
  event: 'Event Jobs',
};

// ─── Group definitions ────────────────────────────────────────────────────────

export const ANALYSIS_GROUP_ORDER = ['overview', 'transmux', 'h264', 'h265'] as const;

export type AnalysisGroupKey = (typeof ANALYSIS_GROUP_ORDER)[number];

export interface AnalysisSeriesGroup {
  title: string;
  subtitle: string;
  recordingKeys: string[];
  transcodingKeys: string[];
}

export const ANALYSIS_SERIES_GROUPS: Record<string, AnalysisSeriesGroup> = {
  overview: {
    title: 'Overview',
    subtitle: 'Total duration, audio-only workloads, and transmuxing.',
    recordingKeys: ['total', 'audioOnly', 'transmux'],
    transcodingKeys: ['total', 'audio_only', 'transmux'],
  },
  transmux: {
    title: 'Transmux',
    subtitle: 'Pass-through repackaging by resolution.',
    recordingKeys: ['sdTransmux', 'hdTransmux', 'uhdTransmux'],
    transcodingKeys: ['transmux'],
  },
  h264: {
    title: 'H.264 Encoding',
    subtitle: 'H.264 CPU and GPU variants.',
    recordingKeys: [
      'h264SdTranscoding', 'h264HdTranscoding', 'h264UhdTranscoding',
      'gpuH264SdTranscoding', 'gpuH264HdTranscoding', 'gpuH264UhdTranscoding',
    ],
    transcodingKeys: [
      'h264_sd_transcoding', 'h264_hd_transcoding', 'h264_uhd_transcoding',
      'gpu_h264_sd_transcoding', 'gpu_h264_hd_transcoding', 'gpu_h264_uhd_transcoding',
    ],
  },
  h265: {
    title: 'H.265 Encoding',
    subtitle: 'H.265 CPU and GPU variants.',
    recordingKeys: [
      'h265SdTranscoding', 'h265HdTranscoding', 'h265UhdTranscoding',
      'gpuH265SdTranscoding', 'gpuH265HdTranscoding', 'gpuH265UhdTranscoding',
    ],
    transcodingKeys: [
      'h265_sd_transcoding', 'h265_hd_transcoding', 'h265_uhd_transcoding',
      'gpu_h265_sd_transcoding', 'gpu_h265_hd_transcoding', 'gpu_h265_uhd_transcoding',
    ],
  },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

export const toNumber = (value?: string | number | null): number => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const monthIndexFromValue = (value: string): number => {
  const parsed = new Date(value);
  const index = parsed.getMonth();
  return Number.isFinite(index) ? index : 0;
};

const visibleMonthIndices = (
  selectedYear: number,
  currentYear: number,
  currentMonthIndex: number,
): number[] => {
  if (selectedYear !== currentYear) return MONTH_LABELS.map((_, i) => i);
  const startIndex = Math.max(0, currentMonthIndex - 4);
  return MONTH_LABELS.map((_, i) => i).slice(startIndex, currentMonthIndex + 1);
};

const formatDayLabel = (value: string): string => {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: '2-digit' }).format(parsed);
};

// ─── Chart data builders — return @vnetwork/ui Chart-compatible shape ─────────

/** One row per x-point: { label, [seriesKey]: number, ... } */
export type ChartRow = Record<string, string | number>;

export interface BuiltChartData {
  rows: ChartRow[];
  series: ChartSeries[];
}

export function buildRecordingData(
  rows: LmsRecordingDurationUsage[],
  year: number,
  currentYear: number,
  currentMonthIndex: number,
  keys: string[],
): BuiltChartData {
  const byMonth = new Map<number, LmsRecordingDurationUsage>();
  rows.forEach((row) => byMonth.set(monthIndexFromValue(row.month), row));
  const indices = visibleMonthIndices(year, currentYear, currentMonthIndex);

  const chartRows: ChartRow[] = indices.map((monthIndex) => {
    const src = byMonth.get(monthIndex) as Record<string, unknown> | undefined;
    const point: ChartRow = { label: MONTH_LABELS[monthIndex] };
    for (const key of keys) {
      point[key] = toNumber(src?.[key] as string | number | null | undefined);
    }
    return point;
  });

  return {
    rows: chartRows,
    series: keys.map((key) => ({ key, label: RECORDING_LABELS[key] ?? key })),
  };
}

export function buildTranscodingData(
  rows: LmsTranscodingDurationUsage[],
  keys: string[],
): BuiltChartData {
  const sorted = [...rows].sort(
    (a, b) => new Date(a.spot_time_str).getTime() - new Date(b.spot_time_str).getTime(),
  );

  const chartRows: ChartRow[] = sorted.map((row) => {
    const src = row as unknown as Record<string, unknown>;
    const point: ChartRow = { label: formatDayLabel(row.spot_time_str) };
    for (const key of keys) {
      point[key] = toNumber(src[key] as number | null | undefined);
    }
    return point;
  });

  return {
    rows: chartRows,
    series: keys.map((key) => ({ key, label: TRANSCODING_LABELS[key] ?? key })),
  };
}

export function buildJobCountsData(
  rows: LmsJobCountsUsage[],
  year: number,
  currentYear: number,
  currentMonthIndex: number,
): BuiltChartData {
  const keys = ['h264MaxConcurrentNum', 'h265MaxConcurrentNum', 'linear', 'event'];
  const byMonth = new Map<number, LmsJobCountsUsage>();
  rows.forEach((row) => byMonth.set(monthIndexFromValue(row.month), row));
  const indices = visibleMonthIndices(year, currentYear, currentMonthIndex);

  const chartRows: ChartRow[] = indices.map((monthIndex) => {
    const src = byMonth.get(monthIndex) as Record<string, unknown> | undefined;
    const point: ChartRow = { label: MONTH_LABELS[monthIndex] };
    for (const key of keys) {
      point[key] = toNumber(src?.[key] as string | number | null | undefined);
    }
    return point;
  });

  return {
    rows: chartRows,
    series: keys.map((key) => ({ key, label: JOB_LABELS[key] ?? key })),
  };
}
